import { useCallback, useState } from 'react';
import { useArticleTextSelectionHandler } from './useArticleTextSelectionHandler';
import { getHighlightDataFromRange } from '../services/getHighlightDataFromRange/getHighlightDataFromRange';
import { ARTICLE_BLOCK_CLASSNAME } from '../../ArticleBodyRenderer/constants';
import { closestNode } from '../../../services/dom/closestNode';
import { getFeatureFlag } from '../../FeatureFlags/getFeatureFlag';
import { TArticleHighlight } from '../types';
import { TArticleBody } from '../../ArticleBodyRenderer/types';

export type TOnHighlightCreate = (highlightData: TArticleHighlight) => void;

const selectionChangedHandlerFactory = (onHighlightCreate: TOnHighlightCreate, body: TArticleBody) => () => {
  const selection = window.getSelection();

  if (!selection) {
    return;
  }
  if (selection.rangeCount <= 0) {
    return;
  }

  const range = selection.getRangeAt(0);

  if (!range) {
    return;
  }
  if (range.collapsed) {
    return;
  }
  if (!closestNode(range.endContainer, `.${ARTICLE_BLOCK_CLASSNAME}`)) {
    return;
  }
  if (!closestNode(range.startContainer, `.${ARTICLE_BLOCK_CLASSNAME}`)) {
    return;
  }

  onHighlightCreate(getHighlightDataFromRange(range, body as TArticleBody));
};

export const useArticleHighlightCreate = (
  onHighlightCreate: TOnHighlightCreate,
  body: TArticleBody,
): TArticleHighlight | undefined => {
  if (!getFeatureFlag('replyToQuote')) {
    return;
  }

  const [highlightData, setHighlightData] = useState<TArticleHighlight>([]);
  const selectionChangedHandler = useCallback(
    selectionChangedHandlerFactory((highlightData: TArticleHighlight) => {
      onHighlightCreate(highlightData);
      setHighlightData(highlightData);
    }, body),
    [onHighlightCreate, setHighlightData],
  );

  useArticleTextSelectionHandler(selectionChangedHandler, [body]);

  return highlightData;
};
